import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Building, Users, CheckCircle } from 'lucide-react';

interface MembershipType {
  id: string;
  name: string;
  price_eur: number;
  description: string;
  benefits: string[];
  discount_percentage: number;
}

const CorporateMemberships = () => {
  const [corporateTypes, setCorporateTypes] = useState<MembershipType[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCorporateTypes();
  }, []);

  const fetchCorporateTypes = async () => {
    const { data, error } = await supabase
      .from('membership_types_2025_12_03_10_30')
      .select('*')
      .like('name', 'Corporate%')
      .order('price_eur', { ascending: true });
    
    if (error) {
      console.error('Error fetching corporate types:', error);
    } else {
      setCorporateTypes(data || []);
    }
    setLoading(false);
  };

  const handleJoinMembership = (membershipName: string) => {
    const routeMap: { [key: string]: string } = {
      'Corporate Bronze': '/corporate-bronze-signup',
      'Corporate Silver': '/corporate-silver-signup',
      'Corporate Gold': '/corporate-gold-signup'
    };
    navigate(routeMap[membershipName] || '/join');
  };

  const handlePortalAccess = (membershipName: string) => {
    const routeMap: { [key: string]: string } = {
      'Corporate Bronze': '/bronze-portal',
      'Corporate Silver': '/silver-portal',
      'Corporate Gold': '/gold-portal'
    };
    navigate(routeMap[membershipName] || '/member-login');
  };

  const getTierIcon = (name: string) => {
    if (name.includes('Bronze')) return '🥉';
    if (name.includes('Silver')) return '🥈';
    if (name.includes('Gold')) return '🥇';
    return '🏢';
  };

  const getTierColor = (name: string) => {
    if (name.includes('Bronze')) return 'from-amber-600 to-amber-800';
    if (name.includes('Silver')) return 'from-gray-400 to-gray-600';
    if (name.includes('Gold')) return 'from-yellow-400 to-yellow-600';
    return 'from-primary to-primary-glow';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building className="h-12 w-12 text-primary mx-auto mb-4" />
          <p>Loading corporate memberships...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <Building className="h-16 w-16 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Corporate Memberships
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            For companies and organisations operating in the security, technology, or risk sectors. 
            Automatically invoiced on 1st January each year.
          </p>
        </div>

        {/* Corporate Tiers */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {corporateTypes.map((membership) => (
            <Card 
              key={membership.id} 
              className={`relative overflow-hidden hover:shadow-xl transition-all duration-300 ${
                membership.name.includes('Gold') ? 'ring-2 ring-primary scale-105' : ''
              }`}
            >
              {membership.name.includes('Gold') && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground text-center py-2 text-sm font-semibold">
                  Most Popular
                </div>
              )}
              
              <CardHeader className={`text-center ${membership.name.includes('Gold') ? 'pt-12' : ''}`}>
                <div className="text-4xl mb-4">{getTierIcon(membership.name)}</div>
                <CardTitle className="text-2xl">
                  {membership.name.replace('Corporate ', '')} Tier
                </CardTitle>
                <div className={`text-4xl font-bold bg-gradient-to-r ${getTierColor(membership.name)} bg-clip-text text-transparent`}>
                  €{membership.price_eur}
                </div>
                <div className="text-sm text-muted-foreground">per year</div>
                <CardDescription className="text-base mt-2">
                  {membership.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {membership.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-2">
                  <Button 
                    onClick={() => handleJoinMembership(membership.name)}
                    className={`w-full ${
                      membership.name.includes('Gold') 
                        ? 'bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg' 
                        : ''
                    }`}
                  >
                    Join {membership.name.replace('Corporate ', '')}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handlePortalAccess(membership.name)}
                    className="w-full"
                  >
                    Access Portal
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Information */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Corporate Benefits Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">All Corporate Tiers Include:</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      10% discount on CSI courses for all staff
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Three complimentary Professional Memberships included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Recognition as a CSI Corporate Partner
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Digital badge and certificate
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Access to CSI Corporate Roundtables and Innovation Labs
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Priority sponsorship and branding opportunities at CSI events
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Why Choose Corporate Membership?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Strategic Advantages:</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Enhance your organization's security expertise</li>
                    <li>• Access to cutting-edge research and best practices</li>
                    <li>• Networking with industry leaders and peers</li>
                    <li>• Professional development for your security team</li>
                    <li>• Thought leadership opportunities</li>
                    <li>• Influence industry standards and practices</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium">
                    💡 Need help choosing the right tier? Contact our team for a consultation 
                    to determine the best corporate membership for your organization.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <Card className="text-center">
          <CardContent className="py-12">
            <h2 className="text-3xl font-bold mb-4">Ready to Elevate Your Organization?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join leading organizations in the security industry and unlock exclusive benefits for your team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg transition-all duration-300"
                onClick={() => navigate('/corporate-gold-signup')}
              >
                Start with Gold Tier
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => window.open('mailto:info@convergedsecurity.es?subject=Corporate Membership Inquiry', '_blank')}
              >
                Contact Sales Team
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CorporateMemberships;